// Funnel telemetry that reaches BOTH GA4 and our own database.
//
// Until now the /splitter page only ever fired GA4 beacons. GA4 is denied in
// the EEA/UK (the Studio has no consent banner), silently dropped by ad
// blockers, and invisible from /admin — so "how many people used the splitter"
// had no reliable answer. Every event routed through here is also mirrored to
// POST /api/events/log, where anonymous visitors are attributed to a random
// per-browser id (`rl_anon`) instead of an account. No PII travels: the id is
// random, file names are never sent, and meta is a handful of short scalars.
//
// Best-effort throughout: telemetry must never be able to break the page.
import { track } from './track';

const ANON_KEY = 'rl_anon';

/** A random, per-browser visitor id. Stable across visits until storage is cleared. */
export function anonId(): string {
  try {
    const have = localStorage.getItem(ANON_KEY);
    if (have && /^[A-Za-z0-9-]{8,48}$/.test(have)) return have;
    const fresh = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : 'a' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
    localStorage.setItem(ANON_KEY, fresh);
    return fresh;
  } catch {
    // Private mode / storage blocked: a per-page-load id is the best we can do.
    return memoId;
  }
}
const memoId = 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);

/** Coarse device class. Mirrors the server's rl_device_from_ua() buckets. */
export function deviceKind(): 'mobile' | 'tablet' | 'desktop' {
  try {
    const ua = navigator.userAgent || '';
    if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua)) return 'tablet';
    if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
  } catch { /* ignore */ }
  return 'desktop';
}

/** Host of the page that sent the visitor here, or '' (same-site hops too). */
export function referrerHost(): string {
  try { return document.referrer ? new URL(document.referrer).host.toLowerCase().slice(0, 60) : ''; } catch { return ''; }
}

type Params = Record<string, string | number | boolean | undefined>;

/**
 * Fire one funnel event to GA4 AND the server mirror. `params` are sent to both
 * (the server keeps at most six short scalars — keep them small and stable).
 * `keepalive` lets a request started during pagehide survive the unload.
 */
export function funnelEvent(event: string, params: Params = {}, surface = 'splitter'): void {
  track(event, params);
  try {
    const meta: Params = {};
    let n = 0;
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || n >= 6) continue;
      meta[k] = typeof v === 'string' ? v.slice(0, 60) : v;
      n++;
    }
    const body = JSON.stringify({ event, surface, meta, anon_id: anonId() });
    void fetch('/api/events/log', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* offline / blocked — never surface */ });
  } catch { /* never throw from telemetry */ }
}

/**
 * Remember which funnel a purchase intent came from, so the checkout page can
 * attribute it. sessionStorage survives the /login → Google → back round-trip
 * in the same tab, which is exactly the path an anonymous buyer takes.
 */
export function markFunnel(name: string): void {
  try { sessionStorage.setItem('rl_funnel', name); } catch { /* ignore */ }
}
export function currentFunnel(): string {
  try { return sessionStorage.getItem('rl_funnel') || 'direct'; } catch { return 'direct'; }
}
